import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { 
  Trophy, Clock, Target, Users, Calendar, TrendingUp,
  Award, Star, Zap, Crown, Lock, Loader2, ChevronRight,
  BarChart3, PieChart, Activity, Medal, Flame, Filter, X, Check, Edit2
} from 'lucide-react';
import { toast } from 'sonner';

// Event type definitions with icons and colors
const EVENT_TYPES = [
  { id: 'practice', name: 'Practice', icon: '🏃', color: 'bg-blue-100 text-blue-700' },
  { id: 'scrimmage', name: 'Scrimmage', icon: '⚔️', color: 'bg-slate-100 text-slate-700' },
  { id: 'game', name: 'Game', icon: '🎮', color: 'bg-green-100 text-green-700' },
  { id: 'tournament', name: 'Tournament', icon: '🏅', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'playoff', name: 'Playoff', icon: '🔥', color: 'bg-orange-100 text-orange-700' },
  { id: 'championship', name: 'Championship', icon: '🏆', color: 'bg-purple-100 text-purple-700' },
  { id: 'tryout', name: 'Tryout', icon: '📋', color: 'bg-gray-100 text-gray-700' },
];

// Multi-select filter chip component
const FilterChip = ({ selected, onClick, children, color = '' }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
      selected 
        ? color || 'bg-purple-100 text-purple-700 ring-2 ring-purple-500 ring-offset-1'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
    {selected && <Check className="h-3 w-3" />}
  </button>
);

// Filter section component
const FilterSection = ({ title, children }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue', large = false }) => (
  <div 
    className={`bg-white rounded-2xl p-${large ? '6' : '5'} shadow-sm border border-gray-100 ${large ? 'col-span-2' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className={`${large ? 'text-4xl' : 'text-2xl'} font-bold text-gray-900`}>{value}</p>
        {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`h-6 w-6 text-${color}-600`} />
      </div>
    </div>
  </div>
);

const AchievementBadge = ({ icon, title, description }) => (
  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
    <span className="text-2xl">{icon}</span>
    <div>
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

const ChildStatCard = ({ child, onClick }) => {
  const winRate = child.wins + child.losses + child.ties > 0
    ? Math.round((child.wins / (child.wins + child.losses + child.ties)) * 100)
    : 0;
  
  return (
    <div 
      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-all hover:-translate-y-1"
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold"
          style={{ backgroundColor: child.color || '#3B82F6' }}
        >
          {child.name?.charAt(0) || '?'}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{child.name}</p>
          <p className="text-sm text-gray-500">{child.total_events} events</p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-2 bg-blue-50 rounded-lg">
          <p className="text-lg font-bold text-blue-600">{Math.round(child.practice_minutes / 60)}h</p>
          <p className="text-xs text-gray-500">Practice</p>
        </div>
        <div className="p-2 bg-green-50 rounded-lg">
          <p className="text-lg font-bold text-green-600">{Math.round(child.game_minutes / 60)}h</p>
          <p className="text-xs text-gray-500">Games</p>
        </div>
        <div className="p-2 bg-purple-50 rounded-lg">
          <p className="text-lg font-bold text-purple-600">{child.goals}</p>
          <p className="text-xs text-gray-500">Goals</p>
        </div>
      </div>
      
      {(child.wins > 0 || child.losses > 0 || child.ties > 0) && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Record</span>
            <span className="font-semibold">
              <span className="text-green-600">{child.wins}W</span>
              {' - '}
              <span className="text-red-600">{child.losses}L</span>
              {' - '}
              <span className="text-gray-600">{child.ties}T</span>
            </span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden flex">
            {child.wins > 0 && (
              <div className="h-full bg-green-500" style={{ width: `${(child.wins / (child.wins + child.losses + child.ties)) * 100}%` }} />
            )}
            {child.ties > 0 && (
              <div className="h-full bg-gray-400" style={{ width: `${(child.ties / (child.wins + child.losses + child.ties)) * 100}%` }} />
            )}
            {child.losses > 0 && (
              <div className="h-full bg-red-500" style={{ width: `${(child.losses / (child.wins + child.losses + child.ties)) * 100}%` }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatisticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [aiSummary, setAiSummary] = useState('');
  const [loadingAi, setLoadingAi] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  
  // Filter state
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ children: [], activities: [], event_types: [] });
  
  // Date range state
  const [dateRange, setDateRange] = useState('all'); // 'all', '7', '30', '60', '90', '180', '365', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Child detail modal state
  const [selectedChild, setSelectedChild] = useState(null);
  const [childDetailOpen, setChildDetailOpen] = useState(false);
  const [childEvents, setChildEvents] = useState([]);
  const [loadingChildDetail, setLoadingChildDetail] = useState(false);

  const DATE_RANGE_OPTIONS = [
    { value: 'all', label: 'All Time' },
    { value: '7', label: '7 Days' },
    { value: '30', label: '30 Days' },
    { value: '60', label: '60 Days' },
    { value: '90', label: '90 Days' },
    { value: '180', label: '6 Months' },
    { value: '365', label: '1 Year' },
    { value: 'custom', label: 'Custom' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (accountStatus?.account_type === 'pro') {
      loadFilteredStats();
      // Clear AI summary when filters change so user knows to regenerate
      setAiSummary('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildren, selectedActivities, selectedEventTypes, dateRange, customStartDate, customEndDate]);

  const loadData = async () => {
    try {
      const accountRes = await api.get('/account/status');
      setAccountStatus(accountRes.data);
      
      if (accountRes.data.account_type === 'pro') {
        const statsRes = await api.get('/statistics/overview');
        setStats(statsRes.data);
        if (statsRes.data.filter_options) {
          setFilterOptions(statsRes.data.filter_options);
        }
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredStats = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedChildren.length > 0) {
        params.append('child_ids', selectedChildren.join(','));
      }
      if (selectedActivities.length > 0) {
        params.append('activity_ids', selectedActivities.join(','));
      }
      if (selectedEventTypes.length > 0) {
        params.append('event_types', selectedEventTypes.join(','));
      }
      
      // Add date range params
      if (dateRange !== 'all' && dateRange !== 'custom') {
        params.append('days', dateRange);
      } else if (dateRange === 'custom') {
        if (customStartDate) params.append('start_date', customStartDate);
        if (customEndDate) params.append('end_date', customEndDate);
      }
      
      const statsRes = await api.get(`/statistics/overview?${params.toString()}`);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load filtered statistics:', error);
    }
  };

  const toggleChildFilter = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const toggleActivityFilter = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) 
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const toggleEventTypeFilter = (eventType) => {
    setSelectedEventTypes(prev => 
      prev.includes(eventType) 
        ? prev.filter(type => type !== eventType)
        : [...prev, eventType]
    );
  };

  const clearAllFilters = () => {
    setSelectedChildren([]);
    setSelectedActivities([]);
    setSelectedEventTypes([]);
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const hasActiveFilters = selectedChildren.length > 0 || selectedActivities.length > 0 || selectedEventTypes.length > 0 || dateRange !== 'all';

  const generateAiSummary = async () => {
    setLoadingAi(true);
    try {
      const payload = {};
      if (dateRange !== 'all' && dateRange !== 'custom') {
        payload.days = parseInt(dateRange);
      } else if (dateRange === 'custom') {
        if (customStartDate) payload.start_date = customStartDate;
        if (customEndDate) payload.end_date = customEndDate;
      }
      
      const response = await api.post('/statistics/ai-summary', payload);
      setAiSummary(response.data.summary);
    } catch (error) {
      toast.error('Failed to generate AI summary');
    } finally {
      setLoadingAi(false);
    }
  };

  // Open child detail modal
  const openChildDetail = async (child) => {
    setSelectedChild(child);
    setChildDetailOpen(true);
    setLoadingChildDetail(true);
    
    try {
      // Fetch events for this child
      const response = await api.get(`/events?child_id=${child.id}`);
      // Handle different response formats
      const eventsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.events || response.data?.items || []);
      setChildEvents(eventsData);
    } catch (error) {
      console.error('Failed to load child events:', error);
      setChildEvents([]);
    } finally {
      setLoadingChildDetail(false);
    }
  };

  // Show upgrade prompt for free users
  if (!loading && accountStatus?.account_type !== 'pro') {
    return (
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-purple-600" />
            Statistics & Analytics
          </h1>
          <p className="text-gray-500 mt-2">Track your family's sports journey with detailed insights</p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 p-8 md:p-12 text-white text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-6">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Kid Stats & Analytics
            </h2>
            <p className="text-white/80 max-w-lg mx-auto text-lg">
              Track practice vs game time, record scores and goals, see win/loss records, 
              and get AI-powered insights about your kids' progress.
            </p>
          </div>

          {/* Features Grid - Vertical Stack on Mobile */}
          <div className="p-6 md:p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">What's coming with Pro</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Time Tracking</p>
                  <p className="text-sm text-gray-600">Track practice and game hours per child and activity</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Goals & Assists</p>
                  <p className="text-sm text-gray-600">Record and track scoring stats for each game</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <Trophy className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Win/Loss Record</p>
                  <p className="text-sm text-gray-600">See team records and win percentages over time</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">AI Insights</p>
                  <p className="text-sm text-gray-600">Get personalized summaries and progress reports</p>
                </div>
              </div>
            </div>

            {/* Coming Soon CTA */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-purple-100 text-purple-700 rounded-full text-lg font-medium mb-4">
                <Crown className="h-5 w-5" />
                Pro Features Coming Soon
              </div>
              <p className="text-sm text-gray-500">
                We're working on bringing you these amazing features. Stay tuned!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const overview = stats?.overview || {};
  const byChild = stats?.by_child || [];
  const byActivity = stats?.by_activity || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-purple-600" />
            Statistics & Analytics
          </h1>
          <p className="text-gray-500 mt-1">Track your family's sports journey</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`rounded-full ${showFilters ? 'bg-purple-600' : ''}`}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1.5 w-5 h-5 bg-white text-purple-600 rounded-full text-xs flex items-center justify-center font-bold">
                {selectedChildren.length + selectedActivities.length + selectedEventTypes.length}
              </span>
            )}
          </Button>
          <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-1">
            <Crown className="h-4 w-4" /> Pro
          </span>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filter Statistics
            </h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-gray-500 hover:text-gray-700">
                <X className="h-4 w-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>

          {/* Kids Filter */}
          {filterOptions.children?.length > 0 && (
            <FilterSection title="Kids">
              {filterOptions.children.map(child => (
                <FilterChip
                  key={child.id}
                  selected={selectedChildren.includes(child.id)}
                  onClick={() => toggleChildFilter(child.id)}
                >
                  <span 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: child.color || '#3B82F6' }}
                  />
                  {child.name}
                </FilterChip>
              ))}
            </FilterSection>
          )}

          {/* Activities Filter */}
          {filterOptions.activities?.length > 0 && (
            <FilterSection title="Activities">
              {filterOptions.activities.map(activity => (
                <FilterChip
                  key={activity.id}
                  selected={selectedActivities.includes(activity.id)}
                  onClick={() => toggleActivityFilter(activity.id)}
                >
                  {activity.name}
                </FilterChip>
              ))}
            </FilterSection>
          )}

          {/* Event Types Filter */}
          <FilterSection title="Event Types">
            {EVENT_TYPES.map(eventType => (
              <FilterChip
                key={eventType.id}
                selected={selectedEventTypes.includes(eventType.id)}
                onClick={() => toggleEventTypeFilter(eventType.id)}
                color={selectedEventTypes.includes(eventType.id) ? eventType.color : ''}
              >
                <span>{eventType.icon}</span>
                {eventType.name}
              </FilterChip>
            ))}
          </FilterSection>

          {/* Date Range Filter */}
          <FilterSection title="Date Range">
            <div className="flex flex-wrap gap-2">
              {DATE_RANGE_OPTIONS.map(option => (
                <FilterChip
                  key={option.value}
                  selected={dateRange === option.value}
                  onClick={() => setDateRange(option.value)}
                >
                  {option.label}
                </FilterChip>
              ))}
            </div>
            
            {/* Custom Date Range Inputs */}
            {dateRange === 'custom' && (
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </FilterSection>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className="pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing stats for: {' '}
                {dateRange !== 'all' && (
                  <span className="font-medium text-purple-600">
                    {dateRange === 'custom' 
                      ? `${customStartDate || 'Start'} - ${customEndDate || 'End'}`
                      : DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label
                    }
                  </span>
                )}
                {selectedChildren.length > 0 && (
                  <span className="font-medium text-purple-600">
                    {dateRange !== 'all' ? ', ' : ''}{selectedChildren.length} kid{selectedChildren.length > 1 ? 's' : ''}
                  </span>
                )}
                {selectedActivities.length > 0 && (
                  <span className="font-medium text-purple-600">
                    {(selectedChildren.length > 0 || dateRange !== 'all') ? ', ' : ''}{selectedActivities.length} activit{selectedActivities.length > 1 ? 'ies' : 'y'}
                  </span>
                )}
                {selectedEventTypes.length > 0 && (
                  <span className="font-medium text-purple-600">
                    {(selectedChildren.length > 0 || selectedActivities.length > 0 || dateRange !== 'all') ? ', ' : ''}{selectedEventTypes.length} event type{selectedEventTypes.length > 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* AI Summary Card */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Zap className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-bold text-lg">SmartDaddy AI Insights</h2>
              <p className="text-white/70 text-sm">Your personalized sports summary</p>
            </div>
          </div>
          <Button 
            onClick={generateAiSummary}
            disabled={loadingAi}
            className="bg-white/20 hover:bg-white/30 text-white rounded-full"
          >
            {loadingAi ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            {aiSummary ? 'Refresh' : 'Generate'}
          </Button>
        </div>
        {aiSummary ? (
          <div 
            className="text-white/95 leading-relaxed prose prose-invert prose-sm max-w-none
              [&>p]:mb-2 [&>ul]:my-2 [&>ul]:ml-4 [&>ul>li]:mb-1
              [&_strong]:text-white [&_strong]:font-bold"
            dangerouslySetInnerHTML={{ 
              __html: aiSummary
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n•/g, '<br/>•')
                .replace(/\n/g, '<br/>')
            }} 
          />
        ) : (
          <p className="text-white/60 italic text-sm">
            Click "Generate" for a quick AI-powered snapshot of your family's sports stats!
          </p>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Calendar} 
          label="Total Events" 
          value={overview.total_events || 0}
          subValue={`${overview.practice_events || 0} practices, ${overview.game_events || 0} games`}
          color="blue"
        />
        <StatCard 
          icon={Clock} 
          label="Total Time" 
          value={`${overview.total_hours || 0}h`}
          subValue={`${overview.practice_hours || 0}h practice, ${overview.game_hours || 0}h games`}
          color="green"
        />
        <StatCard 
          icon={Target} 
          label="Goals Scored" 
          value={overview.total_goals || 0}
          subValue={`${overview.total_assists || 0} assists`}
          color="orange"
        />
        <StatCard 
          icon={Trophy} 
          label="Win Rate" 
          value={
            (overview.record?.wins || 0) + (overview.record?.losses || 0) > 0
              ? `${Math.round((overview.record?.wins / ((overview.record?.wins || 0) + (overview.record?.losses || 0))) * 100)}%`
              : 'N/A'
          }
          subValue={`${overview.record?.wins || 0}W - ${overview.record?.losses || 0}L - ${overview.record?.ties || 0}T`}
          color="purple"
        />
      </div>

      {/* Practice vs Game Time Visual */}
      {(overview.practice_hours > 0 || overview.game_hours > 0) && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChart className="h-5 w-5 text-purple-600" />
            Practice vs Game Time
          </h3>
          <div className="flex items-center gap-8">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="20"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="20"
                  strokeDasharray={`${(overview.practice_hours / overview.total_hours) * 251.2} 251.2`}
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="20"
                  strokeDasharray={`${(overview.game_hours / overview.total_hours) * 251.2} 251.2`}
                  strokeDashoffset={`-${(overview.practice_hours / overview.total_hours) * 251.2}`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900">{overview.total_hours}h</span>
              </div>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded bg-blue-500" />
                <span className="text-gray-600">Practice</span>
                <span className="ml-auto font-semibold">{overview.practice_hours}h ({Math.round((overview.practice_hours / overview.total_hours) * 100)}%)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded bg-green-500" />
                <span className="text-gray-600">Games & Tournaments</span>
                <span className="ml-auto font-semibold">{overview.game_hours + (overview.tournament_hours || 0)}h ({Math.round(((overview.game_hours + (overview.tournament_hours || 0)) / overview.total_hours) * 100)}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-Child Stats */}
      {byChild.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Stats by Child
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {byChild.map((child, index) => (
              <ChildStatCard 
                key={index} 
                child={child} 
                onClick={() => openChildDetail(child)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-Activity Stats */}
      {byActivity.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-600" />
            Time by Activity
          </h3>
          <div className="space-y-3">
            {byActivity.sort((a, b) => b.total_minutes - a.total_minutes).map((activity, index) => (
              <div key={index} className="flex items-center gap-4">
                <span className="w-32 text-sm text-gray-600 truncate">{activity.name}</span>
                <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                    style={{ 
                      width: `${Math.min((activity.total_minutes / Math.max(...byActivity.map(a => a.total_minutes))) * 100, 100)}%` 
                    }}
                  />
                </div>
                <span className="w-16 text-right text-sm font-medium text-gray-900">
                  {Math.round(activity.total_minutes / 60)}h
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {overview.total_events === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
          <Medal className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Statistics Yet</h3>
          <p className="text-gray-500 mb-6">
            Start adding events with scores and details to see your stats come to life!
          </p>
          <Button onClick={() => navigate('/family')} className="rounded-full">
            Add Your First Event
          </Button>
        </div>
      )}

      {/* Child Detail Modal */}
      <Dialog open={childDetailOpen} onOpenChange={setChildDetailOpen}>
        <DialogContent className="sm:max-w-2xl mx-4 rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedChild && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                    style={{ backgroundColor: selectedChild.color || '#3B82F6' }}
                  >
                    {selectedChild.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">{selectedChild.name}'s Stats</DialogTitle>
                    <p className="text-sm text-gray-500">{selectedChild.total_events} total events</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-blue-600">{Math.round(selectedChild.practice_minutes / 60)}h</p>
                    <p className="text-xs text-gray-500">Practice Time</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-green-600">{Math.round(selectedChild.game_minutes / 60)}h</p>
                    <p className="text-xs text-gray-500">Game Time</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-purple-600">{selectedChild.goals || 0}</p>
                    <p className="text-xs text-gray-500">Goals/Points</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-orange-600">{selectedChild.assists || 0}</p>
                    <p className="text-xs text-gray-500">Assists</p>
                  </div>
                </div>

                {/* Win/Loss Record */}
                {(selectedChild.wins > 0 || selectedChild.losses > 0 || selectedChild.ties > 0) && (
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-semibold text-gray-900 mb-3">Win/Loss Record</h4>
                    <div className="flex items-center gap-4 mb-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg font-semibold">{selectedChild.wins} Wins</span>
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-lg font-semibold">{selectedChild.losses} Losses</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-semibold">{selectedChild.ties} Ties</span>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
                      {selectedChild.wins > 0 && (
                        <div className="h-full bg-green-500" style={{ width: `${(selectedChild.wins / (selectedChild.wins + selectedChild.losses + selectedChild.ties)) * 100}%` }} />
                      )}
                      {selectedChild.ties > 0 && (
                        <div className="h-full bg-gray-400" style={{ width: `${(selectedChild.ties / (selectedChild.wins + selectedChild.losses + selectedChild.ties)) * 100}%` }} />
                      )}
                      {selectedChild.losses > 0 && (
                        <div className="h-full bg-red-500" style={{ width: `${(selectedChild.losses / (selectedChild.wins + selectedChild.losses + selectedChild.ties)) * 100}%` }} />
                      )}
                    </div>
                    <p className="text-center text-sm text-gray-500 mt-2">
                      Win Rate: {Math.round((selectedChild.wins / (selectedChild.wins + selectedChild.losses + selectedChild.ties)) * 100)}%
                    </p>
                  </div>
                )}

                {/* Recent Events */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Recent Events</h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-xl"
                      onClick={() => {
                        setChildDetailOpen(false);
                        navigate(`/family?tab=events`);
                      }}
                    >
                      <Edit2 className="h-4 w-4 mr-1" /> View All
                    </Button>
                  </div>
                  
                  {loadingChildDetail ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600" />
                    </div>
                  ) : !Array.isArray(childEvents) || childEvents.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No events recorded yet</p>
                  ) : (
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {(Array.isArray(childEvents) ? childEvents : []).slice(0, 10).map((event, idx) => {
                        const eventType = EVENT_TYPES.find(t => t.id === event.event_type) || EVENT_TYPES[0];
                        const hasResult = event.score_home !== null && event.score_away !== null;
                        return (
                          <div key={event.id || idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xl">
                              {eventType.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{event.title}</p>
                              <p className="text-xs text-gray-500">{event.date} • {eventType.name}</p>
                            </div>
                            {hasResult && (
                              <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                                event.score_home > event.score_away 
                                  ? 'bg-green-100 text-green-700' 
                                  : event.score_home < event.score_away 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-gray-100 text-gray-700'
                              }`}>
                                {event.score_home}-{event.score_away}
                              </span>
                            )}
                            {event.goals > 0 && (
                              <span className="text-xs text-gray-500">⚽{event.goals}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setChildDetailOpen(false)} className="rounded-xl">
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setChildDetailOpen(false);
                    navigate(`/family?tab=events`);
                  }} 
                  className="rounded-xl"
                >
                  <Edit2 className="h-4 w-4 mr-2" /> Edit Events
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatisticsPage;
